// Canonical render scenes for the tile-brush composer — one source of truth for
// both the human visual bench (bench/index.html) and the Playwright regression
// (bench/render.pw.js). Each scene is plain data: a set of wall polylines plus
// the grid/render params. Adding a scene here adds it to both the bench grid and
// the screenshot suite.
//
// Coverage spans the whole render vocabulary — straight runs, corners (incl. the
// door-jamb variant), T-junctions, 4-way crosses, endings, diagonals, and the
// seam-connector on/off policy — with the #54 no-overlap inset exercised by
// every corner/T/cross scene.

/**
 * @typedef {object} Scene
 * @property {string} label         short letter (A, B, …) for referring to a scene
 * @property {string} name          stable id (used for the screenshot filename)
 * @property {string} title         human label shown on the bench
 * @property {number} gridW         canvas width in cells
 * @property {number} gridH         canvas height in cells
 * @property {number} cellPx        pixels per cell
 * @property {Array<{id:string,brushId:string,points:Array<{x:number,y:number}>}>} walls
 * @property {string[]} [doorKeys]  "x,y" grid keys treated as door endpoints
 *   (a corner there uses the rounded Corner_D jamb)
 */

const CELL = 64;
const wall = (id, points) => ({ id, brushId: 'stone-earthy-a', points });

/** @type {Scene[]} */
export const SCENES = [
    {
        label: 'A',
        name: 'straight-run',
        title: 'Straight run (6 cells → greedy 3+3)',
        gridW: 8,
        gridH: 3,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 1, y: 1 },
                { x: 7, y: 1 },
            ]),
        ],
    },
    {
        label: 'B',
        name: 'corner',
        title: 'Corner (L) — inset + half-block',
        gridW: 6,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('l', [
                { x: 1, y: 1 },
                { x: 4, y: 1 },
                { x: 4, y: 4 },
            ]),
        ],
    },
    {
        label: 'C',
        name: 'corner-door',
        title: 'Corner at a door (corner-rounded)',
        gridW: 6,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('l', [
                { x: 1, y: 1 },
                { x: 4, y: 1 },
                { x: 4, y: 4 },
            ]),
        ],
        doorKeys: ['4,1'],
    },
    {
        label: 'D',
        name: 'room',
        title: 'Closed room (four corners)',
        gridW: 8,
        gridH: 7,
        cellPx: CELL,
        walls: [
            wall('room', [
                { x: 1, y: 1 },
                { x: 6, y: 1 },
                { x: 6, y: 5 },
                { x: 1, y: 5 },
                { x: 1, y: 1 },
            ]),
        ],
    },
    {
        label: 'E',
        name: 't-junction',
        title: 'T-junction (3-way) — inset both sides + arm',
        gridW: 8,
        gridH: 7,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 1, y: 2 },
                { x: 6, y: 2 },
            ]),
            wall('stub', [
                { x: 3, y: 2 },
                { x: 3, y: 5 },
            ]),
        ],
    },
    {
        label: 'F',
        name: 'cross',
        title: '4-way cross — inset all four arms',
        gridW: 8,
        gridH: 8,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 1, y: 3 },
                { x: 6, y: 3 },
            ]),
            wall('v', [
                { x: 3, y: 1 },
                { x: 3, y: 6 },
            ]),
        ],
    },
    {
        label: 'G',
        name: 'ending',
        title: 'Endings (caps overlap by design)',
        gridW: 6,
        gridH: 3,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 1, y: 1 },
                { x: 4, y: 1 },
            ]),
        ],
    },
    {
        label: 'H',
        name: 'diagonals',
        title: 'Standalone SE diagonals (len 1/2/3/4)',
        gridW: 8,
        gridH: 8,
        cellPx: CELL,
        walls: [
            wall('d1', [
                { x: 1, y: 1 },
                { x: 2, y: 2 },
            ]),
            wall('d2', [
                { x: 4, y: 1 },
                { x: 6, y: 3 },
            ]),
            wall('d3', [
                { x: 1, y: 3 },
                { x: 4, y: 6 },
            ]),
            wall('d4', [
                { x: 3, y: 3 },
                { x: 7, y: 7 },
            ]),
        ],
    },
    {
        label: 'I',
        name: 'axis-diagonal-axis',
        title: 'Crossing diagonals (axis→diag→axis, mirrored)',
        gridW: 9,
        gridH: 5,
        cellPx: CELL,
        walls: [
            wall('run', [
                { x: 1, y: 1 },
                { x: 3, y: 1 },
                { x: 5, y: 3 },
                { x: 7, y: 3 },
            ]),
            wall('mirror', [
                { x: 1, y: 3 },
                { x: 3, y: 3 },
                { x: 5, y: 1 },
                { x: 7, y: 1 },
            ]),
        ],
    },
    {
        label: 'J',
        name: 'long-run',
        title: 'Long run (mixed 1/2/3 packing)',
        gridW: 16,
        gridH: 3,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 1, y: 1 },
                { x: 15, y: 1 },
            ]),
        ],
    },
    {
        label: 'K',
        name: 'asterisk',
        title: 'Asterisk — 4 crossing 2-length lines',
        gridW: 6,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('h', [
                { x: 2, y: 3 },
                { x: 4, y: 3 },
            ]),
            wall('v', [
                { x: 3, y: 2 },
                { x: 3, y: 4 },
            ]),
            wall('se', [
                { x: 2, y: 2 },
                { x: 4, y: 4 },
            ]),
            wall('ne', [
                { x: 2, y: 4 },
                { x: 4, y: 2 },
            ]),
        ],
    },
    {
        label: 'L',
        name: 'south-then-ne',
        title: 'South ×3 then NE ×3 (acute diag junction)',
        gridW: 7,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('run', [
                { x: 2, y: 1 },
                { x: 2, y: 4 },
                { x: 5, y: 1 },
            ]),
        ],
    },
    {
        label: 'M',
        name: 'angled-run',
        title: 'Angled runs (1,1)→(4,2) and (1,3)→(7,5) — true angle, stretched pack',
        gridW: 8,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('short', [
                { x: 1, y: 1 },
                { x: 4, y: 2 },
            ]),
            wall('long', [
                { x: 1, y: 3 },
                { x: 7, y: 5 },
            ]),
        ],
    },
    {
        label: 'N',
        name: 'angled-polyline',
        title: 'Angled run into a south run (1,1)→(4,2)→(4,5) — butt join',
        gridW: 6,
        gridH: 7,
        cellPx: CELL,
        walls: [
            wall('run', [
                { x: 1, y: 1 },
                { x: 4, y: 2 },
                { x: 4, y: 5 },
            ]),
        ],
    },
    {
        label: 'O',
        name: 'angled-corners',
        title: 'Angled bends — obtuse (1,4)→(4,3)→(7,4), sharp (1,2)→(7,1)→(2,1); mitered',
        gridW: 9,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('obtuse', [
                { x: 1, y: 4 },
                { x: 4, y: 3 },
                { x: 7, y: 4 },
            ]),
            wall('sharp', [
                { x: 1, y: 2 },
                { x: 7, y: 1 },
                { x: 2, y: 1 },
            ]),
        ],
    },
    {
        label: 'P',
        name: 'angled-t',
        title: 'T — south stub into angled (1,1)→(7,3) at (4,2); merged',
        gridW: 9,
        gridH: 6,
        cellPx: CELL,
        walls: [
            wall('through', [
                { x: 1, y: 1 },
                { x: 7, y: 3 },
            ]),
            wall('stub', [
                { x: 4, y: 2 },
                { x: 4, y: 5 },
            ]),
        ],
    },
    {
        label: 'Q',
        name: 'angled-crossings',
        title: 'Crossings — angled × axis at (4,2), angled × angled mid-segment',
        gridW: 9,
        gridH: 8,
        cellPx: CELL,
        walls: [
            wall('angled', [
                { x: 1, y: 1 },
                { x: 7, y: 3 },
            ]),
            wall('axis', [
                { x: 4, y: 0 },
                { x: 4, y: 4 },
            ]),
            wall('a1', [
                { x: 1, y: 5 },
                { x: 7, y: 7 },
            ]),
            wall('a2', [
                { x: 2, y: 7 },
                { x: 6, y: 4 },
            ]),
        ],
    },
];
